import { Label } from '../../lib/shadcn/label'
import { Switch } from '../../lib/shadcn/switch'
import { cn } from '../../lib/shadcn/utils'
import type { TriStateValue } from '../../utils/servicing'

/**
 * Whether this report leaves Fleetly, said in plain language before it is sent.
 *
 * The switch is the only control in either incident dialog with a consequence
 * outside the building, so it is set apart and it says who will receive the
 * email rather than leaving the operator to infer it. Suppressing a report is a
 * legitimate act, a car wash note does not need to reach an owner, but it is
 * recorded against the incident so that silence is never mistaken for a bug.
 *
 * `ownerWillBeNotified` comes from v_incident_form_vehicles, which mirrors
 * incident_report_route_v1. The UI describes the database's rule; it does not
 * re-derive it from an email address, because an asset management vehicle can
 * have an owner record the router will never write to.
 */
export function NotifyBox({
  label,
  checked,
  onCheckedChange,
  ownerWillBeNotified,
  ownerName,
  hasVehicle,
  internalReason = null,
}: {
  label: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
  ownerWillBeNotified: boolean | null
  ownerName: string | null
  hasVehicle: boolean
  // vehicles.incidents_internal_reason (migration 9100): set when the owner
  // signed the Manage My Car transfer. Without it the helper would say the car
  // has no owner, which is false and would read as a missing record.
  internalReason?: string | null
}) {
  let helper: string
  if (!hasVehicle) {
    helper = 'Choose a vehicle to see who will be told.'
  } else if (ownerWillBeNotified === null) {
    helper = 'Checking who will be told.'
  } else if (!checked) {
    helper =
      'Nobody will be emailed. The incident is still recorded, and the decision not to send is logged against it.'
  } else if (ownerWillBeNotified) {
    helper = `${ownerName || 'The owner'} will receive this, with the Fleetly incident list copied.`
  } else if (internalReason) {
    helper = `${internalReason}. It goes to the Fleetly team only; ${ownerName || 'the owner'} is not told.`
  } else {
    helper = 'This vehicle has no owner to notify, so it goes to the Fleetly team only.'
  }

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="notify-owner" className="cursor-pointer font-medium">
          {label}
        </Label>
        <Switch id="notify-owner" checked={checked} onCheckedChange={onCheckedChange} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{helper}</p>
    </div>
  )
}

/**
 * Yes, No, or unanswered.
 *
 * A checkbox would force every report into an answer. "Injuries: FALSE" on a
 * question nobody answered is a statement Fleetly cannot stand behind, and it
 * reaches a vehicle owner, so unanswered stays a value the operator can leave
 * alone. This mirrors the fault tri-state in IncidentDetailDialog.
 */
export function TriState({
  id,
  label,
  value,
  onChange,
  allowUnset = true,
  required = false,
}: {
  id: string
  label: string
  value: TriStateValue
  onChange: (v: TriStateValue) => void
  // The incident form requires a real answer, so it hides the third option. The
  // update dialog keeps it, where "not answered" means "leave this alone".
  allowUnset?: boolean
  required?: boolean
}) {
  const options: { code: TriStateValue; label: string }[] = [
    { code: 'yes', label: 'Yes' },
    { code: 'no', label: 'No' },
    ...(allowUnset ? [{ code: 'unset' as TriStateValue, label: 'Not answered' }] : []),
  ]
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </Label>
      <div id={id} className="inline-flex rounded-md border border-border bg-background p-0.5">
        {options.map((o) => (
          <button
            key={o.code}
            type="button"
            onClick={() => onChange(o.code)}
            className={cn(
              'rounded-[5px] px-2.5 py-1.5 text-xs font-medium transition-colors',
              value === o.code
                ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * A field that can honestly be answered "not known yet".
 *
 * Every field on the incident form is required, because a blank on a report that
 * later feeds a fault finding or an insurance claim is indistinguishable from an
 * answer nobody gave. But a case number the police have not issued, and a repair
 * estimate at the roadside, cannot be known when the report is written. Forcing
 * those produces invented facts, which is worse than a blank.
 *
 * So they are still required: the reporter must either give the value or say
 * plainly that it is not known yet, and that choice is recorded.
 */
export function UnknownToggle({
  id,
  unknown,
  onChange,
  label = 'Not known yet',
}: {
  id: string
  unknown: boolean
  onChange: (v: boolean) => void
  label?: string
}) {
  return (
    <button
      id={id}
      type="button"
      onClick={() => onChange(!unknown)}
      aria-pressed={unknown}
      className={cn(
        'rounded-[5px] border px-2 py-1 text-[11px] font-medium transition-colors',
        unknown
          ? 'border-transparent bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
          : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  )
}
